import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import WhatsNew from './WhatsNew';
import './StoreHeader.css';


const StoreHeader: React.FC = () => {
  const { user, logout } = useStoreAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/store/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="store-header">
      <div className="store-header-container">
        <div className="store-header-left">
          <div className="store-header-branding">
            <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="store-header-logo" />
            <div className="store-header-brand-text">
              <div className="store-header-company">Red Bridge Farm Feed Co.</div>
              <div className="store-header-portal-label">Store Portal</div>
            </div>
          </div>
          {user && (
            <nav className={`store-header-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              <Link
                to="/store/dashboard"
                className={`store-nav-link ${isActive('/store/dashboard') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                Dashboard
              </Link>
              <Link
                to="/store/pos"
                className={`store-nav-link store-nav-pos ${isActive('/store/pos') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                POS
              </Link>
              <Link
                to="/store/inventory"
                className={`store-nav-link ${isActive('/store/inventory') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                Inventory
              </Link>
              <Link
                to="/store/orders/new"
                className={`store-nav-link ${isActive('/store/orders') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                New Order
              </Link>
              <Link
                to="/store/orders"
                className={`store-nav-link ${isActive('/store/orders') || isActive('/store/pickup-orders') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                Orders
              </Link>
              <Link
                to="/store/orders-history"
                className={`store-nav-link ${isActive('/store/orders-history') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                Past Orders
              </Link>
              <Link
                to="/store/lockers"
                className={`store-nav-link ${isActive('/store/lockers') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                🔐 Lockers
              </Link>
              <Link
                to="/store/bins"
                className={`store-nav-link ${isActive('/store/bins') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                Bulk Bins
              </Link>
              <Link
                to="/store/scan"
                className={`store-nav-link ${isActive('/store/scan') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                Scan
              </Link>
              <Link
                to="/store/tasks"
                className={`store-nav-link ${isActive('/store/tasks') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                Tasks
              </Link>
              <Link
                to="/store/checkin"
                className={`store-nav-link ${isActive('/store/checkin') ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                Check-In
              </Link>
            </nav>
          )}
        </div>
        {user && (
          <div className="store-header-right">
            {user.can_access_admin_portal && (
              <button
                onClick={() => navigate('/dashboard')}
                className="store-btn-portal-switch"
              >
                Admin Portal
              </button>
            )}
            <div className="store-header-user-info">
              <div className="store-header-welcome">Store Staff</div>
              <div className="store-header-username">
                {user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.username}
              </div>
            </div>
            <span className="store-header-whats-new"><WhatsNew portal="POS" /></span>
            <button onClick={handleLogout} className="store-btn-logout">
              Logout
            </button>
            {/* Hamburger Menu Button */}
            <button
              className={`store-hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              <span className="store-hamburger-line"></span>
              <span className="store-hamburger-line"></span>
              <span className="store-hamburger-line"></span>
            </button>
          </div>
        )}
        {/* Mobile Menu with Portal Links */}
        {user && mobileMenuOpen && (
          <div className="store-mobile-menu">
            <nav className="store-mobile-nav">
              <Link to="/store/dashboard" className={`store-mobile-nav-link ${isActive('/store/dashboard') ? 'active' : ''}`} onClick={handleNavClick}>
                Dashboard
              </Link>
              <Link to="/store/pos" className={`store-mobile-nav-link store-mobile-nav-pos ${isActive('/store/pos') ? 'active' : ''}`} onClick={handleNavClick}>
                POS Checkout
              </Link>
              <Link to="/store/inventory" className={`store-mobile-nav-link ${isActive('/store/inventory') ? 'active' : ''}`} onClick={handleNavClick}>
                Inventory
              </Link>
              <Link to="/store/orders/new" className={`store-mobile-nav-link ${isActive('/store/orders') ? 'active' : ''}`} onClick={handleNavClick}>
                New Order
              </Link>
              <Link to="/store/orders" className={`store-mobile-nav-link ${isActive('/store/orders') || isActive('/store/pickup-orders') ? 'active' : ''}`} onClick={handleNavClick}>
                Orders
              </Link>
              <Link to="/store/orders-history" className={`store-mobile-nav-link ${isActive('/store/orders-history') ? 'active' : ''}`} onClick={handleNavClick}>
                Past Orders
              </Link>
              <Link to="/store/lockers" className={`store-mobile-nav-link ${isActive('/store/lockers') ? 'active' : ''}`} onClick={handleNavClick}>
                🔐 Lockers
              </Link>
              <Link to="/store/bins" className={`store-mobile-nav-link ${isActive('/store/bins') ? 'active' : ''}`} onClick={handleNavClick}>
                Bulk Bins
              </Link>
              <Link to="/store/scan" className={`store-mobile-nav-link ${isActive('/store/scan') ? 'active' : ''}`} onClick={handleNavClick}>
                Scan
              </Link>
              <Link to="/store/tasks" className={`store-mobile-nav-link ${isActive('/store/tasks') ? 'active' : ''}`} onClick={handleNavClick}>
                Tasks
              </Link>
              <Link to="/store/checkin" className={`store-mobile-nav-link ${isActive('/store/checkin') ? 'active' : ''}`} onClick={handleNavClick}>
                Check-In
              </Link>
            </nav>
            <div className="store-mobile-divider"></div>
            <div className="store-mobile-portal-section">
              <div className="store-mobile-section-label">Switch Portal</div>
              <a href="/dashboard" className="store-mobile-portal-link store-portal-admin" onClick={handleNavClick}>
                Admin Portal
              </a>
              <a href="/mill/dashboard" className="store-mobile-portal-link store-portal-mill" onClick={handleNavClick}>
                Mill Portal
              </a>
              <a href="/portal/login" className="store-mobile-portal-link store-portal-customer" target="_blank" rel="noopener noreferrer" onClick={handleNavClick}>
                Customer Portal
              </a>
            </div>
            <div className="store-mobile-divider"></div>
            <button onClick={handleLogout} className="store-mobile-logout-btn">
              Logout
            </button>
          </div>
        )}
      </div>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="store-mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}
    </header>
  );
};

export default StoreHeader;
